(ns practitest.frontend.components2.dashboard.charts.arc-labels
  (:require ["d3" :as d3]
            [practitest.frontend.components2.dashboard.charts.label-layout :as layout]))

(def config
  {:label-ring-multiplier     1.2
   :polyline-start-multiplier 0.95
   :label-spacing             20
   :label-gap                 8
   :label-stagger-step        12
   :label-max-cross-angle     (/ js/Math.PI 24)
   :label-font-min            12
   :label-font-max            14
   :label-font-ratio          0.035
   :label-height              18
   :fade-duration             300
   :fade-delay                300
   :transition-duration       200})

(defn- arc-at-radius
  "D3 arc generator with both radii set to r, so (.centroid gen d) returns
   [x y] at distance r from center along d's slice mid-angle."
  [r]
  (-> (d3/arc) (.innerRadius r) (.outerRadius r)))

(defn- centroid [^js arc ^js d]
  (let [c (.centroid arc d)]
    [(aget c 0) (aget c 1)]))

(defn label-key
  "Key function for D3 data joins — keys by category for stable enter/update/exit."
  [^js d]
  (.. d -data -category))

(defn- ext-text [^js d]
  (aget (.-data d) "value-label"))

(defn label-bounds
  "Visible area for label anchors, in the marks-g coordinate frame (origin =
   pie center). Padding is included since nothing else occupies it for a
   pie chart — it's usable space for labels, not reserved margin."
  [{:keys [padding width height center-x center-y]}]
  {:half-w (+ (:left padding) center-x)
   :top    (+ (:top padding) center-y)
   :bottom (+ (:bottom padding) (- height center-y))})

(defn calc-label-positions
  "Label anchor positions, stacked top to bottom on each side of the pie
   so labels don't overlap each other or other labels' connectors.
   arcs — the d3 pie datum array (already filtered to non-zero slices)."
  [arcs outer-radius bounds]
  (let [{:keys [label-ring-multiplier label-height label-spacing label-gap
                label-stagger-step label-max-cross-angle]} config]
    (layout/layout-labels
     (for [^js d arcs]
       {:key   (label-key d)
        :angle (/ (+ (.-startAngle d) (.-endAngle d)) 2)})
     {:ring-radius     (* outer-radius label-ring-multiplier)
      :bounds          bounds
      :label-height    label-height
      :spacing         label-spacing
      :gap             label-gap
      :stagger-step    label-stagger-step
      :max-cross-angle label-max-cross-angle})))

(defn- label-transform [^js d label-positions]
  (let [{:keys [x y]} (get label-positions (label-key d))]
    (str "translate(" x "," y ")")))

(defn- polyline-points [^js d polyline-arc label-positions]
  (let [[ax ay]       (centroid polyline-arc d)
        {:keys [x y]} (get label-positions (label-key d))]
    (str ax "," ay " " x "," y)))

(defn render-connectors!
  [marks-g arcs outer-radius label-positions]
  (let [{:keys [polyline-start-multiplier fade-duration fade-delay transition-duration]} config
        polyline-arc (arc-at-radius (* outer-radius polyline-start-multiplier))
        points-fn    (fn [^js d] (polyline-points d polyline-arc label-positions))
        lines        (-> marks-g
                        (.selectAll "polyline.connector")
                        (.data arcs label-key))
        entered      (-> (.enter lines)
                        (.append "polyline")
                        (.attr "class" "connector")
                        (.attr "data-index" label-key)
                        (.attr "points" points-fn)
                        (.style "opacity" 0))]

    (-> (.exit lines)
        (.transition) (.duration transition-duration)
        (.style "opacity" 0)
        (.remove))

    (-> entered
        (.transition) (.delay fade-delay) (.duration fade-duration)
        (.style "opacity" 1))

    (-> lines
        (.transition) (.duration transition-duration)
        (.style "opacity" 1)
        (.attr "points" points-fn))))

(defn render-labels!
  [marks-g arcs label-positions {:keys [half-w width]}]
  (let [{:keys [label-font-min label-font-max label-font-ratio label-height
                fade-duration fade-delay transition-duration]} config
        font-size   (-> (* width label-font-ratio)
                        (max label-font-min)
                        (min label-font-max))
        fo-height   label-height
        fo-width-fn (fn [^js d]
                      (let [{:keys [x]} (get label-positions (label-key d))]
                        (-> (- half-w (abs x))
                            (max 0)
                            (min 150))))
        fo-x-fn     (fn [^js d]
                      (let [{:keys [right?]} (get label-positions (label-key d))]
                        (if right? 0 (- (fo-width-fn d)))))
        align-fn    (fn [^js d]
                      (let [{:keys [right?]} (get label-positions (label-key d))]
                        (if right? "flex-start" "flex-end")))
        label-text  (fn [^js d] (str (label-key d) ": "))
        groups      (-> marks-g
                       (.selectAll "g.label-group")
                       (.data arcs label-key))
        entered     (-> (.enter groups)
                       (.append "g")
                       (.attr "class" "label-group")
                       (.attr "data-index" label-key)
                       (.attr "transform" (fn [^js d] (label-transform d label-positions)))
                       (.style "opacity" 0))]

    (-> (.exit groups)
        (.transition) (.duration transition-duration)
        (.style "opacity" 0)
        (.remove))

    (-> entered
        (.transition) (.delay fade-delay) (.duration fade-duration)
        (.style "opacity" 1))

    (let [fo  (-> entered
                  (.append "foreignObject")
                  (.attr "class" "label-fo")
                  (.attr "width" fo-width-fn)
                  (.attr "height" fo-height)
                  (.attr "x" fo-x-fn)
                  (.attr "y" (/ fo-height -2)))
          div (-> fo
                  (.append "xhtml:div")
                  (.attr "class" "label-content")
                  (.style "justify-content" align-fn)
                  (.style "font-size" (str font-size "px")))]
      (-> div
          (.append "xhtml:span")
          (.attr "class" "label")
          (.text label-text))
      (-> div
          (.append "xhtml:span")
          (.attr "class" "label-ext")
          (.text (fn [^js d] (ext-text d)))))

    (-> groups
        (.transition) (.duration transition-duration)
        (.style "opacity" 1)
        (.attr "transform" (fn [^js d] (label-transform d label-positions))))

    (-> groups
        (.select "foreignObject")
        (.attr "width" fo-width-fn)
        (.attr "x" fo-x-fn))

    (-> groups (.select ".label") (.text label-text))
    (-> groups (.select ".label-ext") (.text (fn [^js d] (ext-text d))))
    (-> groups
        (.select ".label-content")
        (.style "justify-content" align-fn)
        (.style "font-size" (str font-size "px")))))

(defn- label-text-width
  "Rendered width of d's label text (category + value spans), or 0 when the
   label isn't in the DOM."
  [marks-g ^js d]
  (let [k       (label-key d)
        content (-> marks-g
                    (.selectAll "g.label-group")
                    (.filter (fn [^js ld] (= (label-key ld) k)))
                    (.select ".label-content")
                    (.node))]
    (if content
      (transduce (map (fn [^js el] (.-offsetWidth el))) + 0 (array-seq (.-children content)))
      0)))

(defn label-anchor
  "Top-center of d's label text, in the marks-g frame (origin = pie center)."
  [marks-g label-positions ^js d]
  (let [{:keys [x y right?]} (get label-positions (label-key d))
        half-text-w          (/ (label-text-width marks-g d) 2)]
    [(if right? (+ x half-text-w) (- x half-text-w))
     (- y (/ (:label-height config) 2))]))

(defn- foreignobjects->text!
  "Replace foreignObject labels with SVG <text> so labels render correctly
   in print/export, which doesn't support foreignObject content."
  [svg]
  (doseq [group (array-seq (.querySelectorAll svg "g.label-group"))]
    (when-let [fo (.querySelector group "foreignObject")]
      (let [label-el  (.querySelector fo ".label")
            ext-el    (.querySelector fo ".label-ext")
            label-txt (when label-el (.-textContent label-el))
            ext-txt   (when ext-el (.-textContent ext-el))
            right?    (= "0" (.getAttribute fo "x"))
            text-el   (.createElementNS js/document "http://www.w3.org/2000/svg" "text")]
        (.setAttribute text-el "text-anchor" (if right? "start" "end"))
        (.setAttribute text-el "dominant-baseline" "central")
        (.setAttribute text-el "font-family" "Quicksand, sans-serif")
        (.setAttribute text-el "font-size" "8")
        (.setAttribute text-el "font-weight" "400")
        (when (seq label-txt)
          (let [tspan (.createElementNS js/document "http://www.w3.org/2000/svg" "tspan")]
            (set! (.-textContent tspan) label-txt)
            (.appendChild text-el tspan)))
        (when (seq ext-txt)
          (let [tspan (.createElementNS js/document "http://www.w3.org/2000/svg" "tspan")]
            (set! (.-textContent tspan) (str " " ext-txt))
            (.appendChild text-el tspan)))
        (.removeChild group fo)
        (.appendChild group text-el)))))

(defn prepare-export!
  [svg-element svg-clone]
  (let [bbox (.getBBox svg-element)
        pad  50
        bx   (- (.-x bbox) pad)
        by   (- (.-y bbox) pad)
        bw   (+ (.-width bbox) (* 2 pad))
        bh   (+ (.-height bbox) (* 2 pad))]
    (foreignobjects->text! svg-clone)
    (.setAttribute svg-clone "viewBox" (str bx " " by " " bw " " bh))
    (.setAttribute svg-clone "width" bw)
    (.setAttribute svg-clone "height" bh)
    svg-clone))
